import * as THREE from 'three';
import { CSS3DRenderer } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass.js';
import { ScreenHTMLLabelMesh } from '../shapes/ScreenHTMLLabelMesh';
import { DeviceInfo } from '../utils/DeviceInfo';
import { Logger } from './Logger';
import { SceneSettingsData } from './SceneSettingsData';
import { AppSettingsData } from './AppSettingsData';
import { Color } from '../types/Color';
import { SceneNode } from './SceneNode';
import { InputEventManager } from './InputEventManager';


export class Scene {
    public static Instance: Scene;

    private threeScene!: THREE.Scene;
    private cssScene!: THREE.Scene;
    private camera!: THREE.PerspectiveCamera;
    private renderer!: THREE.WebGLRenderer;
    private cssRenderer!: CSS3DRenderer;
    private canvas: HTMLCanvasElement;
    private cssContainer: HTMLElement;
    public readonly settings: SceneSettingsData;
    private readonly appSettings: AppSettingsData;

    private ambientLight!: THREE.AmbientLight;
    private keyLight!: THREE.DirectionalLight;
    private fillLight!: THREE.DirectionalLight;
    private backLight!: THREE.DirectionalLight;

    // Post-processing (Depth of Field)
    private _composer: EffectComposer | null = null;
    private _bokehPass: BokehPass | null = null;
    private _renderPass: RenderPass | null = null;

    // Background gradient caching
    private _lastGradientColor1: string | null = null;
    private _lastGradientColor2: string | null = null;
    private _lastGradientAngle: number | null = null;

    // 📱 Screen HTML Labels tracking
    private screenLabels: Set<ScreenHTMLLabelMesh> = new Set();

    constructor(
        canvas: HTMLCanvasElement, 
        cssContainer: HTMLElement, 
        sceneSettings: SceneSettingsData,
        appSettings: AppSettingsData
    ) {
        if (Scene.Instance) {
            throw new Error("Scene is a singleton and has already been instantiated.");
        }
        Scene.Instance = this;

        this.canvas = canvas;
        this.cssContainer = cssContainer;
        this.settings = sceneSettings;
        this.appSettings = appSettings;
        this.initializeThreeJS();
        this.setupLights();
        this.setupPostProcessing();
    }

    private initializeThreeJS(): void {
        this.threeScene = new THREE.Scene();
        this.cssScene = new THREE.Scene(); // Separate scene for CSS3D objects

        const isMobile = DeviceInfo.isMobile;
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: this.settings.antialiasing.value ?? true,
            alpha: true,
            // preserveDrawingBuffer forces a framebuffer copy every frame instead of a cheap
            // buffer swap — a known mobile GPU killer ("GPU stall due to ReadPixels") and a
            // contributor to iOS Safari OOM tab-kills. Only needed for canvas screenshot
            // export, which we support on desktop only.
            preserveDrawingBuffer: !isMobile
        });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        // Cap pixelRatio at 2: iPhone DPR=3 means 2.25× the pixels of DPR=2 for virtually no
        // visible gain, and it pushes mobile GPUs into watchdog/OOM territory on heavy scenes.
        // Debug bisect flag: ?lowdpr=1 forces DPR 1 (quarter the pixels of DPR 2).
        const lowDpr = new URL(window.location.href).searchParams.get('lowdpr') === '1';
        this.renderer.setPixelRatio(lowDpr ? 1 : Math.min(window.devicePixelRatio, 2));

        // Evidence hooks: a GPU crash (context lost) kills the scene silently otherwise.
        // These land in the remote log-api so mobile deaths become diagnosable.
        this.canvas.addEventListener('webglcontextlost', (e) => {
            try { Logger.Instance.Error(`WebGL CONTEXT LOST (GPU reset/OOM likely) — ${(e as any).statusMessage || ''}`, 'WebGL'); } catch {}
        });
        this.canvas.addEventListener('webglcontextrestored', () => {
            try { Logger.Instance.Warn('WebGL context restored', 'WebGL'); } catch {}
        });
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.0;
        this.renderer.setClearColor(0x000000, 0);

        this.cssRenderer = new CSS3DRenderer();
        this.cssRenderer.setSize(window.innerWidth, window.innerHeight);
        this.cssRenderer.domElement.style.position = 'absolute';
        this.cssRenderer.domElement.style.top = '0px';
        this.cssRenderer.domElement.style.pointerEvents = 'none'; // Don't block interactions
        this.cssRenderer.domElement.style.zIndex = '100'; // Above canvas but below UI
        this.cssContainer.appendChild(this.cssRenderer.domElement);

        // Register canvas with InputEventManager for 3D scene interactions
        InputEventManager.Instance.setCanvas(this.canvas);
        InputEventManager.Instance.setScene(this.threeScene);

        window.addEventListener('resize', this.handleResize.bind(this));
    }

    private setupLights(): void {
        // Create lights
        this.ambientLight = new THREE.AmbientLight();
        this.keyLight = new THREE.DirectionalLight();
        this.fillLight = new THREE.DirectionalLight();
        this.backLight = new THREE.DirectionalLight();

        // Add to scene
        this.threeScene.add(this.ambientLight, this.keyLight, this.fillLight, this.backLight);

        // Set initial values from settings
        this.updateLightProperties();
    }

    /**
     * Initialize post-processing pipeline (EffectComposer + BokehPass for DOF).
     * The composer is created lazily once a camera is available, but we wire up
     * property listeners immediately so settings changes take effect at runtime.
     */
    private setupPostProcessing(): void {
        // Listen for DOF property changes and sync to the BokehPass uniforms
        this.settings.dofAperture.on((value: number | null) => {
            if (this._bokehPass && value !== null) {
                (this._bokehPass.uniforms as any)['aperture'].value = value;
            }
        });

        this.settings.dofMaxBlur.on((value: number | null) => {
            if (this._bokehPass && value !== null) {
                (this._bokehPass.uniforms as any)['maxblur'].value = value;
            }
        });

        this.settings.dofFocus.on((value: number | null) => {
            if (this._bokehPass && value !== null) {
                (this._bokehPass.uniforms as any)['focus'].value = value;
            }
        });
    }

    /**
     * Ensure the EffectComposer and passes exist.
     * Called lazily when DOF is enabled and a camera is set.
     */
    private ensureComposer(): void {
        if (this._composer) return;
        if (!this.camera) return;

        this._composer = new EffectComposer(this.renderer);
        this._renderPass = new RenderPass(this.threeScene, this.camera);
        this._composer.addPass(this._renderPass);

        const focus = this.settings.dofFocus.value ?? 0;
        const aperture = this.settings.dofAperture.value ?? 0.025;
        const maxblur = this.settings.dofMaxBlur.value ?? 0.01;

        this._bokehPass = new BokehPass(this.threeScene, this.camera, {
            focus,
            aperture,
            maxblur,
        });
        this._composer.addPass(this._bokehPass);

        // Match the current renderer size
        const size = this.renderer.getSize(new THREE.Vector2());
        this._composer.setSize(size.x, size.y);
    }

    /**
     * Set the DOF focus distance.
     * A value of 0 means auto-focus (caller should compute distance to active POI).
     */
    public setDOFFocusDistance(distance: number): void {
        this.settings.dofFocus.value = distance;
    }

    private updateLightProperties(): void {
        const origin = new THREE.Vector3(0, 0, 0);

        // Ambient Light
        if (this.settings.ambientLightColor.value) this.ambientLight.color.copy(Color.toThree(this.settings.ambientLightColor.value));
        if (this.settings.ambientLightIntensity.value !== null) this.ambientLight.intensity = this.settings.ambientLightIntensity.value;

        // Key Light
        if (this.settings.keyLightColor.value) this.keyLight.color.copy(Color.toThree(this.settings.keyLightColor.value));
        if (this.settings.keyLightIntensity.value !== null) this.keyLight.intensity = this.settings.keyLightIntensity.value;
        if (this.settings.keyLightPosition.value) this.keyLight.position.copy(this.settings.keyLightPosition.value.toThree());
        this.keyLight.lookAt(origin);

        // Fill Light
        if (this.settings.fillLightColor.value) this.fillLight.color.copy(Color.toThree(this.settings.fillLightColor.value));
        if (this.settings.fillLightIntensity.value !== null) this.fillLight.intensity = this.settings.fillLightIntensity.value;
        if (this.settings.fillLightPosition.value) this.fillLight.position.copy(this.settings.fillLightPosition.value.toThree());
        this.fillLight.lookAt(origin);

        // Back Light
        if (this.settings.backLightColor.value) this.backLight.color.copy(Color.toThree(this.settings.backLightColor.value));
        if (this.settings.backLightIntensity.value !== null) this.backLight.intensity = this.settings.backLightIntensity.value;
        if (this.settings.backLightPosition.value) this.backLight.position.copy(this.settings.backLightPosition.value.toThree());
        this.backLight.lookAt(origin);
    }

    private handleResize(): void {
        if (!this.camera || !this.renderer) return;
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.cssRenderer.setSize(window.innerWidth, window.innerHeight);

        if (this._composer) {
            this._composer.setSize(window.innerWidth, window.innerHeight);
        }
    }

    public add(object: THREE.Object3D): void {
        this.threeScene.add(object);
    }

    public remove(object: THREE.Object3D): void {
        this.threeScene.remove(object);
    }

    /**
     * Add CSS3D object to the CSS3D scene
     */
    public addCSS3DObject(object: THREE.Object3D): void {
        this.cssScene.add(object);
        //console.log(`🌐 Added CSS3DObject to scene: ${object.name || 'unnamed'}`);
    }

    /**
     * Remove CSS3D object from the CSS3D scene
     */
    public removeCSS3DObject(object: THREE.Object3D): void {
        this.cssScene.remove(object);
        //console.log(`🗑️ Removed CSS3DObject from scene: ${object.name || 'unnamed'}`);
    }

    /**
     * Get the CSS3D scene (for direct access if needed)
     */
    public getCSS3DScene(): THREE.Scene {
        return this.cssScene;
    }

    /**
     * 📱 Register a screen HTML label for automatic updates
     */
    public registerScreenLabel(label: ScreenHTMLLabelMesh): void {
        this.screenLabels.add(label);
        //console.log(`📱 Registered screen label: ${label.poi.name} (total: ${this.screenLabels.size})`);
    }

    /**
     * 📱 Unregister a screen HTML label
     */
    public unregisterScreenLabel(label: ScreenHTMLLabelMesh): void {
        this.screenLabels.delete(label);
        //console.log(`📱 Unregistered screen label: ${label.poi.name} (total: ${this.screenLabels.size})`);
    }

    /**
     * 📱 Update all screen HTML labels positions and visibility
     */
    private updateScreenLabels(): void {
        if (this.screenLabels.size === 0) return;

        for (const label of this.screenLabels) {
            // Update screen position based on 3D world position (sets target values)
            label.updateScreenPosition(this.camera, this.renderer);
            
            // Apply interpolated position values to actual CSS styling
            label.updateInterpolatedPosition();
            
            // Update visibility based on distance/activation
            label.updateVisibilityBasedOnDistance(this.camera);
        }
    }
    
    public clear(): void {
        // Dispose all registered screen labels (removes their DOM and 3D stubs)
        if (this.screenLabels.size > 0) {
            for (const label of Array.from(this.screenLabels)) {
                try { label.dispose(); } catch {}
            }
            this.screenLabels.clear();
        }

        // Remove all CSS3D objects
        for (let i = this.cssScene.children.length - 1; i >= 0; i--) {
            this.cssScene.remove(this.cssScene.children[i]);
        }

        // Remove all non-light objects from Three.js scene
        for (let i = this.threeScene.children.length - 1; i >= 0; i--) {
            const child = this.threeScene.children[i];
            if (!(child as any).isLight) {
                this.threeScene.remove(child);
            }
        }
    }

    private _sanitizedGeometryIds = new Set<number>();

    /**
     * Scan scene graph for BufferGeometries with NaN position values
     * and sanitize them before Three.js warns during render.
     * Tracks already-checked geometries by id to avoid redundant work.
     */
    private sanitizeSceneGeometries(): void {
        this.threeScene.traverse((obj: any) => {
            const geom = obj.geometry as THREE.BufferGeometry | undefined;
            if (!geom || this._sanitizedGeometryIds.has(geom.id)) return;
            this._sanitizedGeometryIds.add(geom.id);
            const pos = geom.getAttribute('position');
            if (!pos) return;
            const arr = (pos as THREE.BufferAttribute).array as Float32Array;
            let found = false;
            for (let i = 0; i < arr.length; i++) {
                if (!Number.isFinite(arr[i])) { arr[i] = 0; found = true; }
            }
            if (found) {
                pos.needsUpdate = true;
                geom.computeBoundingSphere();
            }
        });
    }

    public render(): void {
        if (!this.camera) return;

        // Update all screen HTML labels
        this.updateScreenLabels();

        this.sanitizeSceneGeometries();

        if (this.settings.dofEnabled.value) {
            this.ensureComposer();
            if (this._composer) {
                this._composer.render();
            } else {
                this.renderer.render(this.threeScene, this.camera);
            }
        } else {
            this.renderer.render(this.threeScene, this.camera);
        }
        this.cssRenderer.render(this.cssScene, this.camera);
    }
    
    public setCamera(camera: THREE.PerspectiveCamera): void {
        this.camera = camera;
        // Also register camera with InputEventManager for raycasting
        InputEventManager.Instance.setCamera(camera);

        // Update post-processing passes with the new camera
        if (this._renderPass) {
            this._renderPass.camera = camera;
        }
        if (this._bokehPass) {
            this._bokehPass.camera = camera;
        }
    }

    public getCamera(): THREE.PerspectiveCamera {
        return this.camera;
    }

    public updateAndRender(deltaTime: number): void {
        this.updateLightProperties();
        this._updateBackground();
        this.render();
    }

    private _updateBackground(): void {
        const color1 = this.settings.backgroundGradientColor1.value?.toHex() ?? null;
        const color2 = this.settings.backgroundGradientColor2.value?.toHex() ?? null;
        const angle = this.settings.backgroundGradientAngle.value ?? null;

        if (
            color1 !== this._lastGradientColor1 ||
            color2 !== this._lastGradientColor2 ||
            angle !== this._lastGradientAngle
        ) {
            if (color1 && color2 && angle !== null && this.canvas.parentElement) {
                this.canvas.parentElement.style.background = `linear-gradient(${angle}deg, ${color1}, ${color2})`;
            }

            // When DOF (post-processing) is active the canvas becomes opaque,
            // hiding the CSS gradient. Set the Three.js scene background as fallback.
            if (color1) {
                this.threeScene.background = new THREE.Color(color1);
            }

            this._lastGradientColor1 = color1;
            this._lastGradientColor2 = color2;
            this._lastGradientAngle = angle;
        }
    }

    public getThreeScene(): THREE.Scene {
        return this.threeScene;
    }

    public getRenderer(): THREE.WebGLRenderer {
        return this.renderer;
    }

    public getAppSettings(): AppSettingsData {
        return this.appSettings;
    }

    public dispose(): void {
        if (this.cssContainer && this.cssRenderer.domElement.parentNode === this.cssContainer) {
            this.cssContainer.removeChild(this.cssRenderer.domElement);
        }

        // Unregister canvas from InputEventManager
        InputEventManager.Instance.setCanvas(null);

        // Dispose post-processing resources
        if (this._composer) {
            this._composer.dispose();
            this._composer = null;
            this._bokehPass = null;
            this._renderPass = null;
        }

        this.renderer.dispose();
        window.removeEventListener('resize', this.handleResize.bind(this));
        (Scene.Instance as any) = null;
    }
}